import { useCallback, useEffect, useRef, useState } from "react"

import type { Move } from "../sortaballs-8-colors/logic"

// Replays the puzzle's opening solver moves (correctMoves) one at a time, using
// the same flight animation as a real move. The player's current state is
// discarded first: the board resets to the initial state, then the moves play
// out. Demo moves don't count toward moveCount and aren't undoable; committed
// moves (the help reveal) are real moves — counted, undoable and persisted.

const SELECT_DELAY_MS = 150
const FLIGHT_SETTLE_MS = 750
const PER_MOVE_MS = SELECT_DELAY_MS + FLIGHT_SETTLE_MS

export interface ReplayOptions {
	// Commit the replayed moves like player moves (help) instead of demoing
	// them (challenge pre-roll and tour previews).
	commit?: boolean
	// Replay only the first `count` solver moves; omitted → all of them.
	count?: number
}

interface UseHelperReplayInput {
	clearSelection: ()=> void
	commitSelectPlate: (index: number)=> void
	demoSelectPlate: (index: number)=> void
	moves: Move[]
	reset: ()=> void
}

export function useHelperReplay(input: UseHelperReplayInput) {
	const {
		clearSelection,
		commitSelectPlate,
		demoSelectPlate,
		moves,
		reset,
	} = input
	const [isPlaying, setIsPlaying] = useState(false)
	const pendingTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())
	const movesRef = useRef(moves)

	const cancel = useCallback(()=> {
		for (const id of pendingTimersRef.current) clearTimeout(id)
		pendingTimersRef.current.clear()
		setIsPlaying(false)
	}, [])

	const schedule = useCallback((fn: ()=> void, delayMs: number)=> {
		const id = setTimeout(()=> {
			pendingTimersRef.current.delete(id)
			fn()
		}, delayMs)
		pendingTimersRef.current.add(id)
	}, [])

	const selectPlateFor = useCallback(
		(commit: boolean | undefined)=> (commit === true ? commitSelectPlate : demoSelectPlate),
		[commitSelectPlate, demoSelectPlate],
	)

	// Stop an in-flight replay when the puzzle changes (new moves array) or the
	// component unmounts — otherwise stale timers would replay the old puzzle's
	// moves onto the new board.
	useEffect(()=> {
		if (movesRef.current !== moves) {
			movesRef.current = moves
			cancel()
		}
	}, [cancel, moves])

	useEffect(()=> cancel, [cancel])

	const startReplay = useCallback(
		(options: ReplayOptions = {})=> {
			const { commit = false, count } = options
			const targets = count === undefined ? moves : moves.slice(0, count)
			if (isPlaying || targets.length === 0) return
			const selectPlate = selectPlateFor(commit)
			setIsPlaying(true)
			reset()
			for (let i = 0; i < targets.length; i++) {
				const move = targets[i]
				if (!move) continue
				const offset = i * PER_MOVE_MS
				schedule(()=> selectPlate(move.from), offset)
				schedule(()=> selectPlate(move.to), offset + SELECT_DELAY_MS)
			}
			schedule(()=> setIsPlaying(false), targets.length * PER_MOVE_MS)
		},
		[isPlaying, moves, reset, schedule, selectPlateFor],
	)

	// Plays exactly one move on the board as it currently stands (no reset) —
	// used by Sortaballs' incremental reveal when the baseline is intact.
	const revealNextMove = useCallback(
		(move: Move | undefined, options: ReplayOptions = {})=> {
			if (isPlaying || !move) return
			const selectPlate = selectPlateFor(options.commit)
			// Drop any player selection first: a pre-selected plate that equals
			// the move's source would otherwise make the first SELECT_PLATE
			// toggle the selection instead of picking the source, so the move
			// never applies.
			clearSelection()
			setIsPlaying(true)
			schedule(()=> selectPlate(move.from), 0)
			schedule(()=> selectPlate(move.to), SELECT_DELAY_MS)
			schedule(()=> setIsPlaying(false), PER_MOVE_MS)
		},
		[clearSelection, isPlaying, schedule, selectPlateFor],
	)

	return { isPlaying, revealNextMove, startReplay }
}
